import { useState, useEffect, useCallback, useRef } from 'react';
import { fetchExams as fetchExamsApi, fetchExamSchedules, fetchScheduleResults, type ExamApiRecord, type ExamScheduleApiRecord, type StudentResultRecord } from '../../services/examApi';
import { fetchClasses, fetchClassSections, type ClassApiRecord, type SectionApiRecord } from '../../services/classApi';
import { fetchStudents, type StudentApiRecord } from '../../services/studentApi';
import { fetchSchool, type SchoolApiRecord } from '../../services/schoolApi';
import { getSchoolId } from '../../services/auth';
import './ViewResults.css';

/* ── Local types ───────────────────────────────────── */

type SubjectResult = {
    schedule_id: string;
    subject_name: string;
    max_marks: number;
    pass_marks: number;
    obtained_marks: number | null;
    exam_date: string;
};

type StudentRow = {
    id: string;
    name: string;
    roll_number: number;
    initials: string;
    subjects: SubjectResult[];
    total_max: number;
    total_obtained: number;
    percentage: number;
    rank: number;
    status: 'pass' | 'fail' | 'grace';
    subjects_failed: number;
};

const GRACE_MARGIN = 5; // marks margin for grace
const PER_PAGE = 20;

export default function ViewResults() {
    /* ── Filter state ── */
    const [selExam, setSelExam] = useState('');
    const [selClass, setSelClass] = useState('');
    const [selSection, setSelSection] = useState('');

    /* ── Dropdown options ── */
    const [exams, setExams] = useState<ExamApiRecord[]>([]);
    const [classes, setClasses] = useState<ClassApiRecord[]>([]);
    const [sections, setSections] = useState<SectionApiRecord[]>([]);
    const [dropdownsLoading, setDropdownsLoading] = useState(true);

    /* ── Data state ── */
    const [rows, setRows] = useState<StudentRow[]>([]);
    const [loading, setLoading] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');
    const [page, setPage] = useState(1);

    /* ── Drawer state ── */
    const [drawerStudent, setDrawerStudent] = useState<StudentRow | null>(null);

    /* ── Search trigger state ── */
    const [hasSearched, setHasSearched] = useState(false);
    const canSearch = !!(selExam && selClass);

    /* ── School details for print letterhead ── */
    const [school, setSchool] = useState<SchoolApiRecord | null>(null);

    /* ── Load exams & classes on mount ── */
    useEffect(() => {
        (async () => {
            try {
                const [examsRes, classesRes] = await Promise.all([
                    fetchExamsApi({ perPage: 50, isActive: true }),
                    fetchClasses({ perPage: 100 }),
                ]);
                setExams(examsRes.data);
                setClasses(classesRes.data);
            } catch {
                // silent
            } finally {
                setDropdownsLoading(false);
            }
        })();
    }, []);

    /* ── Load active school details (for print letterhead) ── */
    useEffect(() => {
        const schoolId = getSchoolId();
        if (!schoolId) return;
        let cancelled = false;
        (async () => {
            try {
                const data = await fetchSchool(schoolId);
                if (!cancelled) setSchool(data);
            } catch {
                // silent — print will fall back to a generic header
            }
        })();
        return () => { cancelled = true; };
    }, []);

    /* ── Cascade: sections on class change ── */
    useEffect(() => {
        setSections([]);
        setSelSection('');
        if (!selClass) return;
        let cancelled = false;
        (async () => {
            try {
                const res = await fetchClassSections(selClass, { perPage: 100 });
                if (!cancelled) setSections(res.data);
            } catch {
                if (!cancelled) setSections([]);
            }
        })();
        return () => { cancelled = true; };
    }, [selClass]);

    /* ── Compute student rows from API data ── */
    const buildRows = useCallback((
        students: StudentApiRecord[],
        schedules: ExamScheduleApiRecord[],
        allResults: Map<string, StudentResultRecord[]>,
    ): StudentRow[] => {
        const studentRows: StudentRow[] = students.map((s, idx) => {
            const firstName = s.first_name ?? s.user?.first_name ?? '';
            const lastName = s.last_name ?? s.user?.last_name ?? '';
            const fullName = s.name ?? `${firstName}${lastName ? ' ' + lastName : ''}`.trim();
            const initials = `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase() || '?';

            // Build per-subject results
            const subjects: SubjectResult[] = schedules.map((sch) => {
                const results = allResults.get(sch.id) ?? [];
                const result = results.find((r) => r.student_id === s.id);
                return {
                    schedule_id: sch.id,
                    subject_name: sch.subject?.board_subject_name ?? 'Unknown',
                    max_marks: Number(sch.max_marks) || 100,
                    pass_marks: Number(sch.pass_marks) || 33,
                    obtained_marks: result?.obtained_marks != null ? Number(result.obtained_marks) : null,
                    exam_date: sch.exam_date,
                };
            });

            const totalMax = subjects.reduce((sum, sub) => sum + sub.max_marks, 0);
            const totalObtained = subjects.reduce((sum, sub) => sum + (sub.obtained_marks ?? 0), 0);
            const percentage = totalMax > 0 ? (totalObtained / totalMax) * 100 : 0;

            // Determine pass/fail per subject
            let subjectsFailed = 0;
            let totalDeficit = 0;
            for (const sub of subjects) {
                if (sub.obtained_marks === null) continue;
                if (sub.obtained_marks < sub.pass_marks) {
                    subjectsFailed++;
                    totalDeficit += sub.pass_marks - sub.obtained_marks;
                }
            }

            let status: 'pass' | 'fail' | 'grace' = 'pass';
            if (subjectsFailed > 0) {
                // Grace: failed in 1 subject by a small margin
                if (subjectsFailed === 1 && totalDeficit <= GRACE_MARGIN) {
                    status = 'grace';
                } else {
                    status = 'fail';
                }
            }

            return {
                id: s.id,
                name: fullName,
                roll_number: s.roll_number ? Number(s.roll_number) : idx + 1,
                initials,
                subjects,
                total_max: totalMax,
                total_obtained: totalObtained,
                percentage,
                rank: 0,
                status,
                subjects_failed: subjectsFailed,
            };
        });

        // Compute ranks (by percentage, descending)
        const sorted = [...studentRows].sort((a, b) => b.percentage - a.percentage);
        let rank = 0;
        let prevPct = -1;
        for (let i = 0; i < sorted.length; i++) {
            if (sorted[i].percentage !== prevPct) {
                rank = i + 1;
                prevPct = sorted[i].percentage;
            }
            sorted[i].rank = rank;
        }
        // Copy ranks back to original order
        const rankMap = new Map(sorted.map((r) => [r.id, r.rank]));
        for (const row of studentRows) {
            row.rank = rankMap.get(row.id) ?? 0;
        }

        return studentRows;
    }, []);

    /* ── Clear stale rows whenever filters change (search is manual) ── */
    useEffect(() => {
        setRows([]);
        setErrorMsg('');
        setHasSearched(false);
        setPage(1);
    }, [selExam, selClass, selSection]);

    /* ── Manual search trigger (button-driven so section is optional) ── */
    const searchSeqRef = useRef(0);
    const runSearch = useCallback(async () => {
        if (!selExam || !selClass) return;
        const seq = ++searchSeqRef.current;
        setLoading(true);
        setErrorMsg('');
        setPage(1);
        setHasSearched(true);
        try {
            // 1. Fetch schedules + students. Section filter is optional.
            const [schedulesRes, studentsRes] = await Promise.all([
                fetchExamSchedules(selExam, { perPage: 200 }),
                fetchStudents({ perPage: 200, classId: selClass, sectionId: selSection || undefined }),
            ]);
            if (seq !== searchSeqRef.current) return;

            // section_id may be null on schedules that apply to all sections of the class.
            // Without a section selected, include any schedule for the class.
            const matchedSchedules = schedulesRes.data.filter(
                (s) => s.class_id === selClass && (!selSection || s.section_id === selSection || !s.section_id)
            );

            if (matchedSchedules.length === 0) {
                setRows([]);
                setErrorMsg('No exam schedules found for the selected combination.');
                return;
            }

            // 2. Fetch results for each schedule in parallel
            const resultsMap = new Map<string, StudentResultRecord[]>();
            const resultPromises = matchedSchedules.map(async (sch) => {
                try {
                    const res = await fetchScheduleResults(selExam, sch.id, { perPage: 200 });
                    resultsMap.set(sch.id, res.data);
                } catch {
                    resultsMap.set(sch.id, []);
                }
            });
            await Promise.all(resultPromises);
            if (seq !== searchSeqRef.current) return;

            // 3. Build rows
            const computed = buildRows(studentsRes.data, matchedSchedules, resultsMap);
            setRows(computed);
        } catch {
            if (seq === searchSeqRef.current) {
                setRows([]);
                setErrorMsg('Failed to load results. Please try again.');
            }
        } finally {
            if (seq === searchSeqRef.current) setLoading(false);
        }
    }, [selExam, selClass, selSection, buildRows]);

    /* ── Pagination ── */
    const totalPages = Math.ceil(rows.length / PER_PAGE);
    const pagedRows = rows.slice((page - 1) * PER_PAGE, page * PER_PAGE);

    /* ── Stats ── */
    const totalStudents = rows.length;
    const passed = rows.filter((r) => r.status === 'pass').length;
    const failed = rows.filter((r) => r.status === 'fail').length;
    const graced = rows.filter((r) => r.status === 'grace').length;
    const avgPct = totalStudents > 0 ? rows.reduce((s, r) => s + r.percentage, 0) / totalStudents : 0;

    /* ── Derived context labels ── */
    const examName = exams.find((e) => e.id === selExam)?.name ?? '';
    const className = classes.find((c) => c.id === selClass)?.name ?? '';
    const sectionName = sections.find((s) => s.id === selSection)?.name ?? '';

    /* ── Print handler ── */
    const printFrameRef = useRef<HTMLIFrameElement | null>(null);

    const handlePrint = useCallback((student: StudentRow) => {
        const statusLabel = student.status === 'pass' ? 'PASS' : student.status === 'fail' ? 'FAIL' : 'GRACE';
        const statusColor = student.status === 'pass' ? '#15803d' : student.status === 'fail' ? '#dc2626' : '#b45309';
        const sectionLine = sectionName ? ` - Section ${sectionName}` : '';

        // Escape any user/API-supplied strings before injecting into HTML.
        const esc = (v: string | number | null | undefined) => String(v ?? '')
            .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;').replace(/'/g, '&#39;');

        // Build the school letterhead from saved school details.
        const schoolName = school?.name ?? 'School';
        const addressParts = [school?.address, school?.city, school?.state, school?.postal_code, school?.country]
            .filter((p): p is string => !!p && p.trim() !== '');
        const addressLine = addressParts.join(', ');
        const contactParts: string[] = [];
        if (school?.contact_phone) contactParts.push(`Phone: ${school.contact_phone}`);
        if (school?.contact_email) contactParts.push(`Email: ${school.contact_email}`);
        if (school?.website) contactParts.push(`Web: ${school.website}`);
        const contactLine = contactParts.join('  •  ');
        const metaParts: string[] = [];
        if (school?.school_code) metaParts.push(`School Code: ${school.school_code}`);
        const primaryBoard = school?.boards?.[0];
        if (primaryBoard) {
            const affNo = primaryBoard.affiliation?.affiliation_number;
            metaParts.push(affNo ? `${primaryBoard.code} Affiliation No: ${affNo}` : `Affiliated to ${primaryBoard.code}`);
        }
        const metaLine = metaParts.join('  •  ');

        const letterheadHtml = `
<div class="letterhead">
    <h1 class="school-name">${esc(schoolName)}</h1>
    ${addressLine ? `<p class="school-line">${esc(addressLine)}</p>` : ''}
    ${contactLine ? `<p class="school-line">${esc(contactLine)}</p>` : ''}
    ${metaLine ? `<p class="school-line school-meta">${esc(metaLine)}</p>` : ''}
</div>`;

        const subjectRows = student.subjects.map((sub) => {
            const subPassed = sub.obtained_marks !== null && sub.obtained_marks >= sub.pass_marks;
            const subLabel = sub.obtained_marks === null ? '—' : subPassed ? 'Pass' : 'Fail';
            const subColor = sub.obtained_marks === null ? '#9ca3af' : subPassed ? '#15803d' : '#dc2626';
            return `<tr>
                <td style="padding:8px 12px;border-bottom:1px solid #e5e7eb;text-align:left">${esc(sub.subject_name)}</td>
                <td style="padding:8px 12px;border-bottom:1px solid #e5e7eb;text-align:center">${sub.max_marks}</td>
                <td style="padding:8px 12px;border-bottom:1px solid #e5e7eb;text-align:center">${sub.pass_marks}</td>
                <td style="padding:8px 12px;border-bottom:1px solid #e5e7eb;text-align:center;font-weight:600">${sub.obtained_marks ?? '—'}</td>
                <td style="padding:8px 12px;border-bottom:1px solid #e5e7eb;text-align:center;color:${subColor};font-weight:600">${subLabel}</td>
            </tr>`;
        }).join('');

        const html = `<!DOCTYPE html>
<html><head><title>Result - ${esc(student.name)}</title>
<style>
    @page { margin: 18mm 15mm; }
    body { font-family: 'Segoe UI', system-ui, -apple-system, sans-serif; color: #1e293b; margin: 0; padding: 0; }
    .letterhead { text-align: center; padding-bottom: 12px; border-bottom: 3px double #1e293b; margin-bottom: 14px; }
    .letterhead .school-name { font-size: 24px; margin: 0 0 4px 0; letter-spacing: 0.5px; font-weight: 800; text-transform: uppercase; color: #0f172a; }
    .letterhead .school-line { font-size: 11px; color: #475569; margin: 2px 0; line-height: 1.4; }
    .letterhead .school-meta { font-size: 10px; color: #64748b; margin-top: 4px; font-weight: 600; letter-spacing: 0.02em; }
    .report-title { text-align: center; margin-bottom: 22px; }
    .report-title h2 { font-size: 15px; margin: 0; font-weight: 700; text-transform: uppercase; letter-spacing: 0.12em; color: #1e293b; display: inline-block; padding: 4px 18px; border: 1px solid #1e293b; border-radius: 4px; }
    .report-title p { font-size: 12px; color: #475569; margin: 6px 0 0; }
    .info-grid { display: flex; gap: 32px; margin-bottom: 20px; }
    .info-block { flex: 1; }
    .info-label { font-size: 10px; font-weight: 700; color: #6b7280; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 2px; }
    .info-value { font-size: 14px; font-weight: 600; }
    table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
    th { padding: 8px 12px; background: #f1f5f9; font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; color: #475569; text-align: left; border-bottom: 2px solid #cbd5e1; }
    th.center { text-align: center; }
    .total-row td { background: #f8fafc; font-weight: 700; border-top: 2px solid #cbd5e1; }
    .summary { display: flex; gap: 20px; padding: 16px; background: #f8fafc; border-radius: 8px; border: 1px solid #e2e8f0; }
    .summary-item { flex: 1; text-align: center; }
    .summary-item .label { font-size: 10px; font-weight: 700; color: #6b7280; text-transform: uppercase; letter-spacing: 0.5px; }
    .summary-item .value { font-size: 20px; font-weight: 700; margin-top: 2px; }
    .footer { margin-top: 40px; display: flex; justify-content: space-between; font-size: 12px; color: #6b7280; }
    .signature-line { border-top: 1px solid #94a3b8; width: 160px; text-align: center; padding-top: 4px; }
</style></head><body>
${letterheadHtml}
<div class="report-title">
    <h2>Examination Result</h2>
    <p>${esc(examName)} &nbsp;•&nbsp; ${esc(className)}${esc(sectionLine)}</p>
</div>

<div class="info-grid">
    <div class="info-block"><div class="info-label">Student Name</div><div class="info-value">${esc(student.name)}</div></div>
    <div class="info-block"><div class="info-label">Roll Number</div><div class="info-value">${student.roll_number}</div></div>
    <div class="info-block"><div class="info-label">Rank</div><div class="info-value">#${student.rank}</div></div>
</div>

<table>
    <thead><tr>
        <th>Subject</th>
        <th class="center">Max Marks</th>
        <th class="center">Pass Marks</th>
        <th class="center">Obtained</th>
        <th class="center">Status</th>
    </tr></thead>
    <tbody>
        ${subjectRows}
        <tr class="total-row">
            <td style="padding:8px 12px">Total</td>
            <td style="padding:8px 12px;text-align:center">${student.total_max}</td>
            <td style="padding:8px 12px;text-align:center">—</td>
            <td style="padding:8px 12px;text-align:center">${student.total_obtained}</td>
            <td style="padding:8px 12px;text-align:center"></td>
        </tr>
    </tbody>
</table>

<div class="summary">
    <div class="summary-item"><div class="label">Percentage</div><div class="value">${student.percentage.toFixed(1)}%</div></div>
    <div class="summary-item"><div class="label">Rank</div><div class="value">#${student.rank}</div></div>
    <div class="summary-item"><div class="label">Result</div><div class="value" style="color:${statusColor}">${statusLabel}</div></div>
</div>

<div class="footer">
    <div class="signature-line">Class Teacher</div>
    <div class="signature-line">Principal</div>
</div>
</body></html>`;

        // Use a hidden iframe to print
        let iframe = printFrameRef.current;
        if (!iframe) {
            iframe = document.createElement('iframe');
            iframe.style.position = 'fixed';
            iframe.style.left = '-9999px';
            iframe.style.top = '-9999px';
            iframe.style.width = '0';
            iframe.style.height = '0';
            document.body.appendChild(iframe);
            printFrameRef.current = iframe;
        }
        const doc = iframe.contentDocument ?? iframe.contentWindow?.document;
        if (!doc) return;
        doc.open();
        doc.write(html);
        doc.close();
        // Wait for content to render before printing
        setTimeout(() => {
            iframe!.contentWindow?.print();
        }, 250);
    }, [examName, className, sectionName, school]);

    return (
        <div className="vr">
            {errorMsg && (
                <div className="vr__toast vr__toast--error">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" /></svg>
                    {errorMsg}
                    <button className="vr__toast-dismiss" onClick={() => setErrorMsg('')}>&times;</button>
                </div>
            )}

            <div className="vr__header">
                <div className="vr__header-text">
                    <h1 className="vr__title">View Results</h1>
                    <p className="vr__subtitle">Review student results across subjects for an exam.</p>
                </div>
            </div>

            {/* Filters */}
            <div className="vr__filter-card">
                <div className="vr__filter-grid">
                    <div className="vr__filter-field">
                        <label className="vr__filter-label" htmlFor="vrExam">Exam</label>
                        <select className="vr__filter-select" id="vrExam" value={selExam} disabled={dropdownsLoading} onChange={(e) => setSelExam(e.target.value)}>
                            <option value="">Select Exam...</option>
                            {exams.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
                        </select>
                    </div>
                    <div className="vr__filter-field">
                        <label className="vr__filter-label" htmlFor="vrClass">Class</label>
                        <select className="vr__filter-select" id="vrClass" value={selClass} disabled={dropdownsLoading} onChange={(e) => setSelClass(e.target.value)}>
                            <option value="">Select Class...</option>
                            {classes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                    </div>
                    <div className="vr__filter-field">
                        <label className="vr__filter-label" htmlFor="vrSection">Section <span className="vr__filter-optional">(optional)</span></label>
                        <select className="vr__filter-select" id="vrSection" value={selSection} disabled={!selClass || sections.length === 0} onChange={(e) => setSelSection(e.target.value)}>
                            <option value="">All Sections</option>
                            {sections.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                        </select>
                    </div>
                    <div className="vr__filter-field vr__filter-field--action">
                        <button
                            type="button"
                            className="vr__search-btn"
                            disabled={!canSearch || loading}
                            onClick={runSearch}
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
                            {loading ? 'Searching…' : 'Search'}
                        </button>
                    </div>
                </div>
            </div>

            {/* Body */}
            {!hasSearched ? (
                <div className="vr__empty-state">
                    <div className="vr__empty-icon">
                        <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                            <polyline points="14 2 14 8 20 8" />
                            <line x1="16" y1="13" x2="8" y2="13" />
                            <line x1="16" y1="17" x2="8" y2="17" />
                        </svg>
                    </div>
                    <h3 className="vr__empty-title">Select Filters to View Results</h3>
                    <p className="vr__empty-text">Choose an Exam and Class (Section is optional), then click Search.</p>
                </div>
            ) : loading ? (
                <div className="vr__loading"><div className="vr__loader"><span className="vr__loader-dot" /><span className="vr__loader-dot" /><span className="vr__loader-dot" /></div></div>
            ) : rows.length === 0 && !errorMsg ? (
                <div className="vr__empty-state">
                    <div className="vr__empty-icon">
                        <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="12" cy="12" r="10" />
                            <line x1="8" y1="12" x2="16" y2="12" />
                        </svg>
                    </div>
                    <h3 className="vr__empty-title">No Students Found</h3>
                    <p className="vr__empty-text">No students are enrolled in the selected class and section.</p>
                </div>
            ) : (
                <>
                    {/* Stats bar */}
                    <div className="vr__stats-bar">
                        <div className="vr__stat">
                            <span className="vr__stat-value">{totalStudents}</span>
                            <span className="vr__stat-label">Students</span>
                        </div>
                        <div className="vr__stat vr__stat--pass">
                            <span className="vr__stat-value">{passed}</span>
                            <span className="vr__stat-label">Passed</span>
                        </div>
                        <div className="vr__stat vr__stat--fail">
                            <span className="vr__stat-value">{failed}</span>
                            <span className="vr__stat-label">Failed</span>
                        </div>
                        <div className="vr__stat vr__stat--grace">
                            <span className="vr__stat-value">{graced}</span>
                            <span className="vr__stat-label">Grace</span>
                        </div>
                        <div className="vr__stat-spacer" />
                        <div className="vr__stat">
                            <span className="vr__stat-value">{avgPct.toFixed(1)}%</span>
                            <span className="vr__stat-label">Avg %</span>
                        </div>
                    </div>

                    {/* Results table */}
                    <div className="vr__grid-wrap">
                        <table className="vr__table">
                            <thead>
                                <tr>
                                    <th className="vr__th vr__th--name">Student Name</th>
                                    <th className="vr__th vr__th--rank">Rank</th>
                                    <th className="vr__th vr__th--roll">Roll No</th>
                                    <th className="vr__th vr__th--total">Total Marks</th>
                                    <th className="vr__th vr__th--obtained">Obtained</th>
                                    <th className="vr__th vr__th--pct">Percentage</th>
                                    <th className="vr__th vr__th--status">Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {pagedRows.map((r) => (
                                    <tr
                                        className={`vr__row vr__row--${r.status}`}
                                        key={r.id}
                                    >
                                        <td className="vr__td vr__td--name">
                                            <button
                                                className="vr__student-btn"
                                                onClick={() => setDrawerStudent(r)}
                                                title="View details"
                                            >
                                                <span className="vr__avatar">{r.initials}</span>
                                                <span className="vr__student-name">{r.name}</span>
                                            </button>
                                        </td>
                                        <td className="vr__td vr__td--rank">{r.rank}</td>
                                        <td className="vr__td vr__td--roll">{r.roll_number}</td>
                                        <td className="vr__td vr__td--total">{r.total_max}</td>
                                        <td className="vr__td vr__td--obtained">{r.total_obtained}</td>
                                        <td className="vr__td vr__td--pct">{r.percentage.toFixed(1)}%</td>
                                        <td className="vr__td vr__td--status">
                                            <span className={`vr__badge vr__badge--${r.status}`}>
                                                {r.status === 'pass' ? 'Pass' : r.status === 'fail' ? 'Fail' : 'Grace'}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div className="vr__pagination">
                            <span className="vr__page-info">
                                Showing {(page - 1) * PER_PAGE + 1}–{Math.min(page * PER_PAGE, rows.length)} of {rows.length}
                            </span>
                            <div className="vr__page-btns">
                                <button className="vr__page-btn" disabled={page <= 1} onClick={() => setPage(1)} title="First page">
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="11 17 6 12 11 7" /><polyline points="18 17 13 12 18 7" /></svg>
                                </button>
                                <button className="vr__page-btn" disabled={page <= 1} onClick={() => setPage(page - 1)} title="Previous">
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6" /></svg>
                                </button>
                                <span className="vr__page-num">Page {page} of {totalPages}</span>
                                <button className="vr__page-btn" disabled={page >= totalPages} onClick={() => setPage(page + 1)} title="Next">
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6" /></svg>
                                </button>
                                <button className="vr__page-btn" disabled={page >= totalPages} onClick={() => setPage(totalPages)} title="Last page">
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="13 17 18 12 13 7" /><polyline points="6 17 11 12 6 7" /></svg>
                                </button>
                            </div>
                        </div>
                    )}
                </>
            )}

            {/* Student detail drawer */}
            {drawerStudent && (
                <>
                    <div className="vr__backdrop" onClick={() => setDrawerStudent(null)} />
                    <aside className="vr__drawer" role="dialog" aria-label="Student Result Details">
                        <header className="vr__drawer-header">
                            <h2 className="vr__drawer-title">Result Details</h2>
                            <div className="vr__drawer-actions">
                                <button
                                    className="vr__print-btn"
                                    onClick={() => handlePrint(drawerStudent)}
                                    title="Print result"
                                >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 6 2 18 2 18 9" /><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" /><rect x="6" y="14" width="12" height="8" /></svg>
                                    Print
                                </button>
                                <button className="vr__drawer-close" onClick={() => setDrawerStudent(null)}>
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                                </button>
                            </div>
                        </header>

                        <div className="vr__drawer-body">
                            {/* Exam context */}
                            <section className="vr__drawer-section">
                                <div className="vr__drawer-context">
                                    <div className="vr__drawer-context-item">
                                        <span className="vr__drawer-context-label">Exam</span>
                                        <span className="vr__drawer-context-value">{examName}</span>
                                    </div>
                                    <div className="vr__drawer-context-item">
                                        <span className="vr__drawer-context-label">Class</span>
                                        <span className="vr__drawer-context-value">{className}{sectionName ? ` — ${sectionName}` : ''}</span>
                                    </div>
                                </div>
                            </section>

                            {/* Student info */}
                            <section className="vr__drawer-section">
                                <div className="vr__drawer-student">
                                    <span className="vr__drawer-avatar">{drawerStudent.initials}</span>
                                    <div>
                                        <div className="vr__drawer-name">{drawerStudent.name}</div>
                                        <div className="vr__drawer-meta">Roll No. {drawerStudent.roll_number}</div>
                                    </div>
                                </div>

                                <div className="vr__drawer-summary">
                                    <div className="vr__drawer-summary-item">
                                        <span className="vr__drawer-summary-label">Total</span>
                                        <span className="vr__drawer-summary-value">{drawerStudent.total_obtained} / {drawerStudent.total_max}</span>
                                    </div>
                                    <div className="vr__drawer-summary-item">
                                        <span className="vr__drawer-summary-label">Percentage</span>
                                        <span className="vr__drawer-summary-value">{drawerStudent.percentage.toFixed(1)}%</span>
                                    </div>
                                    <div className="vr__drawer-summary-item">
                                        <span className="vr__drawer-summary-label">Rank</span>
                                        <span className="vr__drawer-summary-value">#{drawerStudent.rank}</span>
                                    </div>
                                    <div className="vr__drawer-summary-item">
                                        <span className="vr__drawer-summary-label">Status</span>
                                        <span className={`vr__badge vr__badge--${drawerStudent.status}`}>
                                            {drawerStudent.status === 'pass' ? 'Pass' : drawerStudent.status === 'fail' ? 'Fail' : 'Grace'}
                                        </span>
                                    </div>
                                </div>
                            </section>

                            {/* Subject-wise breakdown */}
                            <section className="vr__drawer-section">
                                <h3 className="vr__drawer-section-title">Subject-wise Marks</h3>
                                <table className="vr__drawer-table">
                                    <thead>
                                        <tr>
                                            <th className="vr__drawer-th">Subject</th>
                                            <th className="vr__drawer-th vr__drawer-th--num">Max</th>
                                            <th className="vr__drawer-th vr__drawer-th--num">Pass</th>
                                            <th className="vr__drawer-th vr__drawer-th--num">Obtained</th>
                                            <th className="vr__drawer-th vr__drawer-th--status">Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {drawerStudent.subjects.map((sub) => {
                                            const subPassed = sub.obtained_marks !== null && sub.obtained_marks >= sub.pass_marks;
                                            const subStatus = sub.obtained_marks === null ? 'pending' : subPassed ? 'pass' : 'fail';
                                            return (
                                                <tr className={`vr__drawer-row vr__drawer-row--${subStatus}`} key={sub.schedule_id}>
                                                    <td className="vr__drawer-td">{sub.subject_name}</td>
                                                    <td className="vr__drawer-td vr__drawer-td--num">{sub.max_marks}</td>
                                                    <td className="vr__drawer-td vr__drawer-td--num">{sub.pass_marks}</td>
                                                    <td className="vr__drawer-td vr__drawer-td--num">
                                                        {sub.obtained_marks !== null ? sub.obtained_marks : '—'}
                                                    </td>
                                                    <td className="vr__drawer-td vr__drawer-td--status">
                                                        <span className={`vr__mini-badge vr__mini-badge--${subStatus}`}>
                                                            {subStatus === 'pass' ? 'Pass' : subStatus === 'fail' ? 'Fail' : '—'}
                                                        </span>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                    <tfoot>
                                        <tr className="vr__drawer-total-row">
                                            <td className="vr__drawer-td vr__drawer-td--bold">Total</td>
                                            <td className="vr__drawer-td vr__drawer-td--num vr__drawer-td--bold">{drawerStudent.total_max}</td>
                                            <td className="vr__drawer-td vr__drawer-td--num">—</td>
                                            <td className="vr__drawer-td vr__drawer-td--num vr__drawer-td--bold">{drawerStudent.total_obtained}</td>
                                            <td className="vr__drawer-td" />
                                        </tr>
                                    </tfoot>
                                </table>
                            </section>
                        </div>
                    </aside>
                </>
            )}
        </div>
    );
}
